import { createBigDecimal } from '@0xtorch/big-decimal'
import { z } from 'zod'

export const tradeSchema = z.object({
  /** Instrument type */
  instType: z.union([
    z.literal('SPOT'),
    z.literal('MARGIN'),
    z.literal('SWAP'),
    z.literal('FUTURES'),
    z.literal('OPTION'),
  ]),
  /** Instrument ID, e.g. `BTC-USDT` */
  instId: z.string(),
  /** Last trade ID */
  tradeId: z.string(),
  /** Order ID */
  ordId: z.string(),
  /** Client Order ID as assigned by the client */
  clOrdId: z.string(),
  /** Bill ID */
  billId: z.string(),
  /** Transaction type */
  subType: z.string(),
  /** Order tag */
  tag: z.string(),
  /** Last filled price */
  fillPx: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Last filled quantity */
  fillSz: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Index price at the moment of trade execution */
  fillIdxPx: z.union([
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
    z.literal('').transform(() => undefined),
  ]),
  /** Last filled profit and loss, applicable to orders which have a trade and aim to close position. It always is 0 in other conditions */
  fillPnl: z.string(),
  /** Implied volatility when filled */
  fillPxVol: z.union([
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
    z.literal('').transform(() => undefined),
  ]),
  /** Options price when filled, in the unit of USD */
  fillPxUsd: z.union([
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
    z.literal('').transform(() => undefined),
  ]),
  /** Mark volatility when filled */
  fillMarkVol: z.union([
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
    z.literal('').transform(() => undefined),
  ]),
  /** Forward price when filled */
  fillFwdPx: z.union([
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
    z.literal('').transform(() => undefined),
  ]),
  /** Mark price when filled */
  fillMarkPx: z.union([
    z.literal('FUTURES'),
    z.literal('SWAP'),
    z.literal('OPTION'),
    z.literal(''),
  ]),
  /** Order side */
  side: z.union([z.literal('buy'), z.literal('sell')]),
  /** Position side */
  posSide: z.union([z.literal('long'), z.literal('short'), z.literal('net')]),
  /** Liquidity taker or maker */
  execType: z.union([z.literal('T'), z.literal('M')]),
  /** Trading fee or rebate currency */
  feeCcy: z.string(),
  /** The amount of trading fee or rebate. */
  fee: z
    .string()
    .regex(/^-?\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Data generation time, Unix timestamp format in milliseconds */
  ts: z
    .string()
    .regex(/^\d+$/)
    .transform((v) => Number.parseInt(v, 10)),
  /** Trade time which is the same as fillTime for the order channel */
  fillTime: z
    .string()
    .regex(/^\d+$/)
    .transform((v) => Number.parseInt(v, 10)),
})

export type Trade = z.infer<typeof tradeSchema>
